import { get, save } from '@rover/react-lib/src/apis/personSummary.api';
import type {
  ActionCreator,
  Duck,
  DuckOptions,
  EffectCreator,
  SelectorCreator,
} from '@rover/react-lib/src/lib/redux-duck';
import { createDuck } from '@rover/react-lib/src/lib/redux-duck';
import type { Address } from '@rover/types/src/Address';
import type { PersonSummary } from '@rover/types/src/PersonSummary';
import type { Pet } from '@rover/types/src/Pet';

export const createEmptyPersonSummary = (): PersonSummary => ({
  firstName: '',
  shortName: '',
  defaultImage: {
    caption: '',
    small: '',
    medium: '',
    wide: '',
    wideRetina: '',
    largeUncropped: '',
    largeUncroppedRetina: '',
    bigThumb: '',
    isFallback: true,
    originalHeight: 0,
    originalWidth: 0,
    orientation: 'square',
    url: '',
    urls: { ordering: '' },
    index: 0,
    public: false,
    added: '',
    uploader: undefined,
    id: '',
  },
  reviewsCount: 0,
  ratingsAverage: '0.0',
  ratingsCount: 0,
  url: '',
  webUrl: '',
  deepLinkUrl: '',
  urls: {
    summary: '',
    private: '',
    images: '',
    services: '',
    videos: '',
    reviews: '',
    promotionAndReferralUrls: '',
    wallet: '',
    walletDefaultPayment: '',
    home: '',
    zipCode: '',
    walletPromos: '',
    walletPromosOnDemand: '',
    stayMedia: '',
    profilePhoto: '',
    coverPhoto: null,
    calendar: '',
    training: {
      trainerProfile: '',
      trainerContact: '',
    },
    publicCalendar: '',
    weekendAvailability: '',
    confirmedAvailability: '',
    hostingServicePreferences: '',
    travelingServicePreferences: '',
    lockbox: '',
    enhancedAddOns: '',
    asRequesterRelationship: '',
    sitterProfile: '',
    webEditProfile: '',
    webPerformanceScores: null,
    webSitterSelfPromotion: '',
    migrationStatus: '',
    calendarSurvey: '',
    messages: '',
  },
  description: '',
  responsiveness: {
    percent: 0,
    percentDesc: '',
    responseTime: 0,
    timeShortDesc: '',
    timeLongDesc: '',
    photoRate: 0,
    photoRateDesc: '',
  },
  images: [],
  imagesCount: 0,
  automaticRecordCount: 0,
  videos: [],
  services: [],
  attributes: {},
  badges: [],
  providerProfile: {
    travelingServicePreferences: {
      acceptedDogsAttributes: [],
      acceptedPetsAttributes: [],
      dogsPreferencesAttributes: [],
      dogsExperienceAttributes: [],
      attributes: [],
    },
    repeatClientCount: 0,
    isActiveStarSitter: false,
  },
  profilePhoto: {
    caption: '',
    small: '',
    medium: '',
    wide: '',
    wideRetina: '',
    largeUncropped: '',
    largeUncroppedRetina: '',
    bigThumb: '',
    isFallback: true,
    originalHeight: 0,
    originalWidth: 0,
    orientation: 'square',
    url: '',
    urls: { ordering: '' },
    index: 0,
    public: false,
    added: '',
    uploader: undefined,
    id: '',
  },
  pets: [],
  memorializedPets: [],
  memberSinceDate: '',
  canContact: false,
  hostingServicePreferences: {
    acceptedDogsAttributes: [],
    acceptedPetsAttributes: [],
    dogsPreferencesAttributes: [],
    dogsExperienceAttributes: [],
    attributes: [],
  },
  private: {
    home: {
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zip: '',
      countryCode: '',
      accessToHome: '',
    },
    emergency_contact_name: '',
    emergency_contact_phone_number: '',
  },
});

export interface State {
  summary: PersonSummary;
  errorMessage: string;
  successMessage: string;
  loading: boolean;
}

type Actions = {
  fetchPersonSummaryPending: ActionCreator<void>;
  fetchPersonSummarySuccess: ActionCreator<PersonSummary>;
  fetchPersonSummaryFailure: ActionCreator<{
    message: string;
  }>;
  savePersonSummaryPrivatePending: ActionCreator<void>;
  savePersonSummaryPrivateSuccess: ActionCreator<PersonSummary['private']>;
  savePersonSummaryPrivateFailure: ActionCreator<{
    message: string;
  }>;
};

type Effects = {
  fetchPersonSummary: EffectCreator<string | null | undefined>;
  savePersonSummaryPrivate: EffectCreator<{
    personData: PersonSummary['private'];
    personUrl: string;
  }>;
};

type Selectors = {
  getPersonSummary: SelectorCreator<PersonSummary>;
  getPersonState: SelectorCreator<string>;
  getErrorMessage: SelectorCreator<string>;
  getPersonUrl: SelectorCreator<string>;
  getIsLoading: SelectorCreator<boolean>;
  getIsSuccessful: SelectorCreator<boolean>;
  getAvailablePets: SelectorCreator<Pet[]>;
  getAddress: SelectorCreator<Address | null | undefined>;
  getOpk: SelectorCreator<string | null | undefined>;
  getEmergencyContactName: SelectorCreator<string>;
  getEmergencyContactPhoneNumber: SelectorCreator<string>;
  getServices: SelectorCreator<unknown[]>;
  getPromotionAndReferralsUrl: SelectorCreator<string | null | undefined>;
};

type Options = DuckOptions<State, Actions, Effects, Selectors>;

export const options: Options = {
  name: 'personSummary',
  initialState: {
    summary: createEmptyPersonSummary(),
    errorMessage: '',
    successMessage: '',
    loading: false,
  },
  actions: {
    fetchPersonSummaryPending: () => (state) => ({ ...state, loading: true }),
    fetchPersonSummarySuccess: (summary) => (state) => ({
      ...state,
      loading: false,
      summary,
      errorMessage: '',
    }),
    fetchPersonSummaryFailure:
      ({ message }) =>
      (state) => ({
        ...state,
        loading: false,
        errorMessage: message,
      }),
    savePersonSummaryPrivatePending: () => (state) => ({ ...state, loading: true }),
    savePersonSummaryPrivateSuccess: (summaryPrivate) => (state) => ({
      ...state,
      loading: false,
      summary: { ...state.summary, private: summaryPrivate },
      errorMessage: '',
    }),
    savePersonSummaryPrivateFailure:
      ({ message }) =>
      (state) => ({
        ...state,
        loading: false,
        errorMessage: message,
      }),
  },
  effects: {
    fetchPersonSummary:
      (personUrl: string | null | undefined) => async (dispatch, getState, duckActions) => {
        dispatch(duckActions.fetchPersonSummaryPending());

        try {
          dispatch(duckActions.fetchPersonSummarySuccess(await get(personUrl)));
        } catch (error) {
          dispatch(duckActions.fetchPersonSummaryFailure(error as Error));
        }
      },
    savePersonSummaryPrivate:
      ({ personData, personUrl }) =>
      async (dispatch, getState, duckActions) => {
        dispatch(duckActions.savePersonSummaryPrivatePending());

        try {
          dispatch(duckActions.savePersonSummaryPrivateSuccess(await save(personData, personUrl)));
        } catch (error) {
          dispatch(duckActions.savePersonSummaryPrivateFailure(error as Error));
        }
      },
  },
  selectors: {
    getPersonSummary: (getState, createSelector) => createSelector([getState], (s) => s.summary),
    getPersonState: (getState, createSelector) =>
      createSelector([getState], (s) => s.summary.private?.home?.state),
    getPersonUrl: (getState, createSelector) => createSelector([getState], (s) => s.summary.url),
    getErrorMessage: (getState, createSelector) =>
      createSelector([getState], (s) => s.errorMessage),
    getIsLoading: (getState, createSelector) => createSelector([getState], (s) => s.loading),
    getIsSuccessful: (getState, createSelector) =>
      createSelector([getState], (s) => !s.loading && !s.errorMessage),
    getAvailablePets: (getState, createSelector) =>
      createSelector([getState], (s) => s.summary.pets),
    getAddress: (getState, createSelector, { getPersonSummary }) =>
      createSelector(
        [getPersonSummary],
        (personSummary) => personSummary && personSummary.private && personSummary.private.home
      ),
    getOpk: (getState, createSelector, { getPersonSummary }) =>
      createSelector(
        [getPersonSummary],
        (personSummary) =>
          personSummary &&
          personSummary.url &&
          personSummary.url
            .substr(0, personSummary.url.length - 1)
            .split('/')
            .pop()
      ),
    getEmergencyContactName: (getState, createSelector, { getPersonSummary }) =>
      createSelector(
        [getPersonSummary],
        (personSummary) => personSummary.private.emergency_contact_name
      ),
    getEmergencyContactPhoneNumber: (getState, createSelector, { getPersonSummary }) =>
      createSelector(
        [getPersonSummary],
        (personSummary) =>
          personSummary &&
          personSummary.private &&
          personSummary.private.emergency_contact_phone_number
      ),
    getServices: (getState, createSelector, { getPersonSummary }) =>
      createSelector(
        [getPersonSummary],
        (personSummary) => personSummary && personSummary.services
      ),
    getPromotionAndReferralsUrl: (getState, createSelector, { getPersonSummary }) =>
      createSelector(
        [getPersonSummary],
        (personSummary) => personSummary && personSummary.urls.promotionAndReferralUrls
      ),
  },
};

export const { actions, selectors, effects, reducer } = createDuck(options) as Duck<
  State,
  Actions,
  Effects,
  Selectors
>;
